// =============================================================================
// ESPOLIDAR WEB ADMIN - TRIGGER FORM
// =============================================================================

import { useState, useEffect } from 'react'
import type { Trigger, TriggerCreate, Zone, Media } from '../types'

interface TriggerFormProps {
  trigger?: Trigger | null
  zones: Zone[]
  mediaList: Media[]
  onSave: (data: TriggerCreate) => void
  onCancel: () => void
}

const TRIGGER_TYPES = [
  { value: 'radar_enter', label: 'Radar - Ingresso' },
  { value: 'radar_exit', label: 'Radar - Uscita' },
  { value: 'lidar_enter', label: 'LIDAR - Ingresso zona' },
  { value: 'lidar_exit', label: 'LIDAR - Uscita zona' },
]

const ACTION_TYPES = [
  { value: 'play_video', label: 'Riproduci Video' },
  { value: 'show_image', label: 'Mostra Immagine' },
  { value: 'play_audio', label: 'Riproduci Audio' },
  { value: 'led_on', label: 'Accendi LED 12V' },
  { value: 'led_off', label: 'Spegni LED 12V' },
  { value: 'ws_color', label: 'LED WS2812B - Accendi (colore)' },
  { value: 'ws_off', label: 'LED WS2812B - Spegni (range)' },
  { value: 'led_clear', label: 'LED WS2812B - Spegni TUTTI' },
]

// Effetti LED disponibili
const LED_EFFECTS = [
  { value: 'solid', label: 'Fisso (nessun effetto)', description: 'Il colore rimane fisso' },
  { value: 'fade_in', label: 'Fade In', description: 'Accensione graduale da spento al colore' },
  { value: 'fade_out', label: 'Fade Out', description: 'Spegnimento graduale dal colore a spento' },
  { value: 'pulse', label: 'Pulsazione', description: 'Pulsazione continua (fade in/out ripetuto)' },
  { value: 'blink', label: 'Lampeggio', description: 'Acceso/spento alternato' },
  { value: 'chase', label: 'Inseguimento', description: 'LED che scorrono in una direzione' },
  { value: 'bounce', label: 'Rimbalzo', description: 'LED che vanno avanti e indietro' },
  { value: 'rainbow', label: 'Arcobaleno', description: 'Scorrimento colori arcobaleno' },
  { value: 'sparkle', label: 'Scintillio', description: 'LED che lampeggiano casualmente' },
  { value: 'wave', label: 'Onda', description: 'Effetto onda di luminosità' },
]

export default function TriggerForm({
  trigger,
  zones,
  mediaList,
  onSave,
  onCancel,
}: TriggerFormProps) {
  const [formData, setFormData] = useState<TriggerCreate>({
    trigger_type: trigger?.trigger_type || 'radar_enter',
    zone_id: trigger?.zone_id || null,
    action_type: trigger?.action_type || 'play_video',
    action_params: trigger?.action_params || {},
    media_id: trigger?.media_id || null,
    priority: trigger?.priority || 0,
    enabled: trigger?.enabled ?? true,
    activation_delay: trigger?.activation_delay ?? 0,
    duration: trigger?.duration ?? 0,
  })

  useEffect(() => {
    if (trigger) {
      setFormData({
        trigger_type: trigger.trigger_type,
        zone_id: trigger.zone_id,
        action_type: trigger.action_type,
        action_params: trigger.action_params || {},
        media_id: trigger.media_id,
        priority: trigger.priority,
        enabled: trigger.enabled,
        activation_delay: trigger.activation_delay ?? 0,
        duration: trigger.duration ?? 0,
      })
    }
  }, [trigger])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onSave(formData)
  }

  const needsZone = formData.trigger_type?.startsWith('lidar_')
  const needsMedia = ['play_video', 'show_image', 'play_audio'].includes(formData.action_type)

  // Filtra media per tipo
  const filteredMedia = mediaList.filter((m) => {
    if (formData.action_type === 'play_video') return m.media_type === 'video'
    if (formData.action_type === 'show_image') return m.media_type === 'image'
    if (formData.action_type === 'play_audio') return m.media_type === 'audio'
    return true
  })

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Tipo trigger */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Tipo Trigger
        </label>
        <select
          value={formData.trigger_type}
          onChange={(e) =>
            setFormData({
              ...formData,
              trigger_type: e.target.value as Trigger['trigger_type'],
              zone_id: e.target.value.startsWith('lidar_') ? formData.zone_id : null,
            })
          }
          className="input"
          required
        >
          {TRIGGER_TYPES.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </select>
      </div>

      {/* Zona (solo per LIDAR) */}
      {needsZone && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Zona LIDAR
          </label>
          <select
            value={formData.zone_id || ''}
            onChange={(e) =>
              setFormData({
                ...formData,
                zone_id: e.target.value ? parseInt(e.target.value) : null,
              })
            }
            className="input"
            required={needsZone}
          >
            <option value="">Seleziona zona...</option>
            {zones.map((z) => (
              <option key={z.id} value={z.id}>
                {z.name} (Zona {z.zone_number})
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Tipo azione */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Azione
        </label>
        <select
          value={formData.action_type}
          onChange={(e) => {
            const newActionType = e.target.value
            let newParams = {}
            if (newActionType === 'ws_color') {
              newParams = { led_start: 0, led_end: 30, color: 0xff0000, effect: 'solid', effect_speed: 500, effect_duration: 0 }
            } else if (newActionType === 'ws_off') {
              newParams = { led_start: 0, led_end: 30 }
            }
            setFormData({
              ...formData,
              action_type: newActionType,
              action_params: newParams,
              media_id: null,
            })
          }}
          className="input"
          required
        >
          {ACTION_TYPES.map((a) => (
            <option key={a.value} value={a.value}>
              {a.label}
            </option>
          ))}
        </select>
      </div>

      {/* Media (se necessario) */}
      {needsMedia && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            File Media
          </label>
          <select
            value={formData.media_id || ''}
            onChange={(e) =>
              setFormData({
                ...formData,
                media_id: e.target.value ? parseInt(e.target.value) : null,
              })
            }
            className="input"
            required={needsMedia}
          >
            <option value="">Seleziona file...</option>
            {filteredMedia.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))}
          </select>
          {filteredMedia.length === 0 && (
            <p className="text-sm text-red-500 mt-1">
              Nessun file disponibile. Carica un file nella sezione Media.
            </p>
          )}
        </div>
      )}

      {/* Parametri aggiuntivi per spegnimento LED WS2812B range */}
      {formData.action_type === 'ws_off' && (
        <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
          <h4 className="font-medium text-gray-900">Spegni LED WS2812B (Range)</h4>

          {/* Range LED */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                LED Inizio
              </label>
              <input
                type="number"
                value={(formData.action_params?.led_start as number) ?? 0}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    action_params: {
                      ...formData.action_params,
                      led_start: parseInt(e.target.value) || 0,
                    },
                  })
                }
                className="input"
                min={0}
                max={299}
              />
              <p className="text-xs text-gray-500 mt-1">Primo LED da spegnere (0 = primo)</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                LED Fine
              </label>
              <input
                type="number"
                value={(formData.action_params?.led_end as number) ?? 30}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    action_params: {
                      ...formData.action_params,
                      led_end: parseInt(e.target.value) || 30,
                    },
                  })
                }
                className="input"
                min={0}
                max={300}
              />
              <p className="text-xs text-gray-500 mt-1">Ultimo LED da spegnere (escluso)</p>
            </div>
          </div>

          {/* Preview */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              LED da spegnere: {(formData.action_params?.led_start as number) ?? 0} - {(formData.action_params?.led_end as number) ?? 30}
            </label>
            <div className="flex gap-0.5 p-2 bg-gray-900 rounded overflow-x-auto">
              {Array.from({ length: Math.min(((formData.action_params?.led_end as number) ?? 30) - ((formData.action_params?.led_start as number) ?? 0), 60) }).map((_, i) => (
                <div
                  key={i}
                  className="w-3 h-3 rounded-sm flex-shrink-0 bg-gray-700 border border-gray-600"
                />
              ))}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {((formData.action_params?.led_end as number) ?? 30) - ((formData.action_params?.led_start as number) ?? 0)} LED verranno spenti
            </p>
          </div>
        </div>
      )}

      {/* Parametri aggiuntivi per azioni LED WS2812B */}
      {formData.action_type === 'ws_color' && (
        <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
          <h4 className="font-medium text-gray-900">Configurazione LED WS2812B</h4>

          {/* Range LED */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                LED Inizio
              </label>
              <input
                type="number"
                value={(formData.action_params?.led_start as number) ?? 0}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    action_params: {
                      ...formData.action_params,
                      led_start: parseInt(e.target.value) || 0,
                    },
                  })
                }
                className="input"
                min={0}
                max={299}
              />
              <p className="text-xs text-gray-500 mt-1">Primo LED (0 = primo)</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                LED Fine
              </label>
              <input
                type="number"
                value={(formData.action_params?.led_end as number) ?? 30}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    action_params: {
                      ...formData.action_params,
                      led_end: parseInt(e.target.value) || 30,
                    },
                  })
                }
                className="input"
                min={0}
                max={300}
              />
              <p className="text-xs text-gray-500 mt-1">Ultimo LED (escluso)</p>
            </div>
          </div>

          {/* Colore */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Colore LED
            </label>
            <div className="flex gap-3 items-center">
              <input
                type="color"
                value={
                  formData.action_params?.color
                    ? `#${(formData.action_params.color as number).toString(16).padStart(6, '0')}`
                    : '#ff0000'
                }
                onChange={(e) => {
                  const color = parseInt(e.target.value.slice(1), 16)
                  setFormData({
                    ...formData,
                    action_params: { ...formData.action_params, color },
                  })
                }}
                className="w-16 h-10 rounded cursor-pointer border"
              />
              <div className="flex gap-2">
                {/* Preset colori */}
                {[
                  { color: 0xff0000, label: 'Rosso' },
                  { color: 0x00ff00, label: 'Verde' },
                  { color: 0x0000ff, label: 'Blu' },
                  { color: 0xffff00, label: 'Giallo' },
                  { color: 0xff00ff, label: 'Magenta' },
                  { color: 0x00ffff, label: 'Ciano' },
                  { color: 0xffffff, label: 'Bianco' },
                ].map((preset) => (
                  <button
                    key={preset.color}
                    type="button"
                    title={preset.label}
                    onClick={() =>
                      setFormData({
                        ...formData,
                        action_params: { ...formData.action_params, color: preset.color },
                      })
                    }
                    className="w-8 h-8 rounded border-2 border-gray-300 hover:border-gray-500"
                    style={{ backgroundColor: `#${preset.color.toString(16).padStart(6, '0')}` }}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Effetto LED */}
          <div className="border-t pt-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Effetto LED
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {LED_EFFECTS.map((effect) => (
                <button
                  key={effect.value}
                  type="button"
                  onClick={() =>
                    setFormData({
                      ...formData,
                      action_params: { ...formData.action_params, effect: effect.value },
                    })
                  }
                  className={`p-2 rounded-lg border-2 text-left transition-all ${
                    (formData.action_params?.effect || 'solid') === effect.value
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="font-medium text-sm">{effect.label}</div>
                  <div className="text-xs text-gray-500">{effect.description}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Parametri effetto (solo se non è "solid") */}
          {(formData.action_params?.effect || 'solid') !== 'solid' && (
            <div className="grid grid-cols-2 gap-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
              <div>
                <label className="block text-sm font-medium text-blue-900 mb-1">
                  Velocità (ms)
                </label>
                <input
                  type="number"
                  value={(formData.action_params?.effect_speed as number) ?? 500}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      action_params: {
                        ...formData.action_params,
                        effect_speed: parseInt(e.target.value) || 500,
                      },
                    })
                  }
                  className="input"
                  min={50}
                  max={5000}
                  step={50}
                />
                <p className="text-xs text-blue-600 mt-1">
                  {(formData.action_params?.effect_speed as number) ?? 500}ms per ciclo
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-blue-900 mb-1">
                  Durata totale (s)
                </label>
                <input
                  type="number"
                  value={(formData.action_params?.effect_duration as number) ?? 0}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      action_params: {
                        ...formData.action_params,
                        effect_duration: parseInt(e.target.value) || 0,
                      },
                    })
                  }
                  className="input"
                  min={0}
                  max={300}
                />
                <p className="text-xs text-blue-600 mt-1">
                  {(formData.action_params?.effect_duration as number) === 0
                    ? 'Continuo (fino al prossimo trigger)'
                    : `Si ferma dopo ${formData.action_params?.effect_duration}s`}
                </p>
              </div>
            </div>
          )}

          {/* Preview */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Anteprima (LED {(formData.action_params?.led_start as number) ?? 0} - {(formData.action_params?.led_end as number) ?? 30})
            </label>
            <div className="flex gap-0.5 p-2 bg-gray-900 rounded overflow-x-auto">
              {Array.from({ length: Math.min(((formData.action_params?.led_end as number) ?? 30) - ((formData.action_params?.led_start as number) ?? 0), 60) }).map((_, i) => (
                <div
                  key={i}
                  className="w-3 h-3 rounded-sm flex-shrink-0"
                  style={{
                    backgroundColor: `#${((formData.action_params?.color as number) ?? 0xff0000).toString(16).padStart(6, '0')}`,
                    boxShadow: `0 0 4px #${((formData.action_params?.color as number) ?? 0xff0000).toString(16).padStart(6, '0')}`,
                  }}
                />
              ))}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {((formData.action_params?.led_end as number) ?? 30) - ((formData.action_params?.led_start as number) ?? 0)} LED selezionati
            </p>
          </div>
        </div>
      )}

      {/* Delay di attivazione (solo per trigger ENTER) */}
      {formData.trigger_type?.includes('_enter') && (
        <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
          <label className="block text-sm font-medium text-amber-900 mb-2">
            Delay di Attivazione
          </label>
          <div className="flex items-center gap-3">
            <input
              type="number"
              value={formData.activation_delay}
              onChange={(e) =>
                setFormData({ ...formData, activation_delay: parseFloat(e.target.value) || 0 })
              }
              className="input w-24 text-center"
              min={0}
              max={10}
              step={0.1}
            />
            <span className="text-sm text-amber-700">secondi</span>
            <div className="flex gap-2 ml-auto">
              {[0, 0.2, 0.5, 1, 2].map((val) => (
                <button
                  key={val}
                  type="button"
                  onClick={() => setFormData({ ...formData, activation_delay: val })}
                  className={`px-2 py-1 rounded text-xs transition-colors ${
                    formData.activation_delay === val
                      ? 'bg-amber-600 text-white'
                      : 'bg-amber-100 text-amber-800 hover:bg-amber-200'
                  }`}
                >
                  {val === 0 ? 'Immediato' : `${val}s`}
                </button>
              ))}
            </div>
          </div>
          <p className="text-xs text-amber-700 mt-2">
            {formData.activation_delay === 0
              ? 'Il trigger si attiva immediatamente all\'ingresso nella zona.'
              : `Il trigger si attiva solo se la zona è occupata per almeno ${formData.activation_delay} secondi.
                 Utile per evitare attivazioni accidentali quando si attraversano zone velocemente.`}
          </p>
        </div>
      )}

      {/* Durata riproduzione (solo per azioni media) */}
      {['play_video', 'show_image', 'play_audio'].includes(formData.action_type) && (
        <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
          <label className="block text-sm font-medium text-blue-900 mb-2">
            Durata Riproduzione
          </label>
          <div className="flex items-center gap-3">
            <input
              type="number"
              value={formData.duration}
              onChange={(e) =>
                setFormData({ ...formData, duration: parseFloat(e.target.value) || 0 })
              }
              className="input w-24 text-center"
              min={0}
              max={3600}
              step={1}
            />
            <span className="text-sm text-blue-700">secondi</span>
            <div className="flex gap-2 ml-auto">
              {[0, 5, 10, 30, 60].map((val) => (
                <button
                  key={val}
                  type="button"
                  onClick={() => setFormData({ ...formData, duration: val })}
                  className={`px-2 py-1 rounded text-xs transition-colors ${
                    formData.duration === val
                      ? 'bg-blue-600 text-white'
                      : 'bg-blue-100 text-blue-800 hover:bg-blue-200'
                  }`}
                >
                  {val === 0 ? 'Auto' : `${val}s`}
                </button>
              ))}
            </div>
          </div>
          <p className="text-xs text-blue-700 mt-2">
            {formData.duration === 0
              ? formData.action_type === 'show_image'
                ? 'L\'immagine rimane visibile fino al prossimo evento.'
                : 'Il file viene riprodotto per la sua durata naturale.'
              : `Il media viene riprodotto per ${formData.duration} secondi, poi si interrompe.`}
          </p>
          <p className="text-xs text-blue-600 mt-1">
            ⚠️ Se si verifica un altro evento, questo media verrà interrotto indipendentemente dalla durata impostata.
          </p>
        </div>
      )}

      {/* Priorità e abilitato */}
      <div className="grid grid-cols-2 gap-4 border-t pt-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Priorità
          </label>
          <input
            type="number"
            value={formData.priority}
            onChange={(e) =>
              setFormData({ ...formData, priority: parseInt(e.target.value) })
            }
            className="input"
            min={0}
            max={100}
          />
          <p className="text-xs text-gray-500 mt-1">
            Priorità più alta viene eseguita prima
          </p>
        </div>
        <div className="flex items-center">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.enabled}
              onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
              className="w-4 h-4 text-primary-600 rounded focus:ring-primary-500"
            />
            <span className="text-sm font-medium text-gray-700">Trigger Abilitato</span>
          </label>
        </div>
      </div>

      {/* Buttons */}
      <div className="flex justify-end gap-3 pt-4 border-t">
        <button type="button" onClick={onCancel} className="btn-secondary">
          Annulla
        </button>
        <button type="submit" className="btn-primary">
          {trigger ? 'Aggiorna Trigger' : 'Crea Trigger'}
        </button>
      </div>
    </form>
  )
}
